import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Referral, ReferralDetailService } from '../../Models/Referral';
import { ClientConfig } from '../../Models/Client-Nav';
import { ReferralService } from '../referral/referral.service';
import { RootScopeService } from '../../core/services/root-scope.service';

@Component({
  selector: 'app-referral-detail',
  templateUrl: './referral-detail.component.html',
  styleUrls: ['./referral-detail.component.scss'],
  standalone: false,
})
export class ReferralDetailComponent implements OnInit {
  public referral: Referral | null = null;
  public loading = false;
  public errorMessage = '';
  public successMessage = '';

  public isDetailsEditMode = false;
  public isServicesEditMode = false;
  public isSaving = false;
  public showValidationErrors = false;
  public editModel: Referral | null = null;

  public services: ReferralDetailService[] = [];

  public typeOfReferralOptions: string[] = [];
  public serviceTypeOptions: string[] = [];
  public actionOptions: string[] = [];
  public vendorNameOptions: string[] = [];
  public vendorNameInput = '';

  private isNewVendorPending: string | null = null;

  public get isNewVendor(): boolean {
    const v = this.vendorNameInput.trim();
    return v.length > 0 && !this.vendorNameOptions.includes(v);
  }

  private referralId = '';
  private pendingEditMode = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly referralService: ReferralService,
    public readonly rootScope: RootScopeService
  ) {}

  public canEditReferral(): boolean {
    return this.rootScope.editGbReferralsVisible() === true;
  }

  ngOnInit(): void {
    this.referralId = this.route.snapshot.queryParamMap.get('id') ?? '';
    const shouldEdit = this.route.snapshot.queryParamMap.get('edit') === 'true';
    if (!this.referralId) {
      this.errorMessage = 'Referral ID is missing.';
      return;
    }
    this.pendingEditMode = shouldEdit;
    this.loadConfigOptions();
    this.loadReferral();
  }

  public onEditDetails(): void {
    if (!this.canEditReferral()) {
      return;
    }
    if (!this.referral) return;
    this.isDetailsEditMode = true;
    this.showValidationErrors = false;
    this.editModel = { ...this.referral };
    this.vendorNameInput = this.referral.vendorName?.trim() ?? '';
    if (this.editModel.typeOfReferral) {
      this.editModel.typeOfReferral = this.matchOption(this.editModel.typeOfReferral, this.typeOfReferralOptions);
    }
    if (this.editModel.serviceTypes) {
      this.editModel.serviceTypes = this.matchOption(this.editModel.serviceTypes, this.serviceTypeOptions);
    }
  }

  public onCancelDetailsEdit(): void {
    this.isDetailsEditMode = false;
    this.showValidationErrors = false;
    this.editModel = null;
    this.vendorNameInput = '';
  }

  public onSaveDetails(): void {
    if (!this.canEditReferral()) {
      return;
    }
    if (!this.editModel || !this.referral?.id) return;

    if (this.isRequiredFieldsMissing()) {
      this.showValidationErrors = true;
      this.errorMessage = 'Type of Referral and Claim Number are required.';
      this.successMessage = '';
      return;
    }

    this.isSaving = true;
    this.showValidationErrors = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.isNewVendorPending = this.isNewVendor ? this.vendorNameInput.trim() : null;

    const vendorValue = this.vendorNameInput.trim() || null;
    const payload = {
      ...this.editModel,
      vendorName: vendorValue,
      services: this.referral.services ?? [],
    };
    const userId = this.rootScope.globals.currentUserId!;

    this.referralService.updateReferral(this.referral.id, payload, userId).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDetailsEditMode = false;
        this.successMessage = 'Referral details updated successfully.';
        if (this.isNewVendorPending) {
          this.saveVendorToConfig(this.isNewVendorPending);
          this.isNewVendorPending = null;
        }
        this.vendorNameInput = '';
        this.loadReferral();
      },
      error: (error: unknown) => {
        this.isSaving = false;
        this.errorMessage = 'Failed to update referral details.';
        console.error('Failed to update referral', error);
      },
    });
  }

  public onEditServices(): void {
    if (!this.canEditReferral()) {
      return;
    }
    if (!this.referral) return;

    // Ensure dropdowns are populated before entering services edit mode.
    this.hydrateEditDropdownsFromCurrentReferral();

    this.isServicesEditMode = true;
    this.services = (this.referral.services ?? []).map(s => ({
      ...s,
      serviceType: s.serviceType ? this.matchOption(s.serviceType, this.serviceTypeOptions) : s.serviceType,
      action: s.action ? this.matchOption(s.action, this.actionOptions) : s.action,
    }));
  }

  public onCancelServicesEdit(): void {
    this.isServicesEditMode = false;
    this.services = (this.referral?.services ?? []).map(s => ({ ...s }));
  }

  public onSaveServices(): void {
    if (!this.canEditReferral()) {
      return;
    }
    if (!this.referral?.id) return;

    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    const payload = {
      ...this.referral,
      services: this.services,
    };
    const userId = this.rootScope.globals.currentUserId!;

    this.referralService.updateReferral(this.referral.id, payload, userId).subscribe({
      next: () => {
        this.isSaving = false;
        this.isServicesEditMode = false;
        this.successMessage = 'Services updated successfully.';
        this.loadReferral();
      },
      error: (error: unknown) => {
        this.isSaving = false;
        this.errorMessage = 'Failed to update services.';
        console.error('Failed to update services', error);
      },
    });
  }

  public onAddService(): void {
    if (!this.canEditReferral()) {
      return;
    }
    this.services = [...this.services, this.createEmptyService()];
  }

  private saveVendorToConfig(vendorName: string): void {
    this.referralService.addConfigValue('vendor_name', vendorName).subscribe({
      next: () => {
        if (!this.vendorNameOptions.includes(vendorName)) {
          this.vendorNameOptions = [...this.vendorNameOptions, vendorName]
            .sort((a, b) => a.localeCompare(b));
        }
      },
      error: (err: unknown) => {
        console.error('Failed to save new vendor to config', err);
      },
    });
  }

  public onRemoveService(index: number): void {
    if (!this.canEditReferral()) {
      return;
    }
    this.services = this.services.filter((_, i) => i !== index);
  }

  private loadReferral(): void {
    this.loading = true;
    this.errorMessage = '';

    this.referralService.getReferralById(this.referralId).subscribe({
      next: (referral: Referral) => {
        this.referral = referral;
        this.services = (referral.services ?? []).map(s => ({ ...s }));
        this.loading = false;
        if (this.pendingEditMode && this.canEditReferral()) {
          this.pendingEditMode = false;
          this.onEditDetails();
        }
      },
      error: (error: unknown) => {
        this.loading = false;
        this.errorMessage = 'Failed to load referral details.';
        console.error('Failed to load referral', error);
      },
    });
  }

  private loadConfigOptions(): void {
    this.referralService.getConfigList().subscribe({
      next: (configs: ClientConfig[]) => {
        // Keep aliases aligned with referral create popup config mapping.
        this.typeOfReferralOptions = this.getConfigValues(configs, ['referralType']);
        this.serviceTypeOptions = this.getConfigValues(configs, ['service_types']);
        this.actionOptions = this.getConfigValuesByConfigKey(configs, 'service_actions');
        this.vendorNameOptions = this.getConfigValues(configs, ['vendor_name', 'vendorname', 'referral_vendors']);

        // Keep current referral values selectable even if backend config misses them.
        this.hydrateEditDropdownsFromCurrentReferral();
      },
      error: (error: unknown) => {
        console.error('Failed to load config options', error);
      },
    });
  }

  private getConfigValuesByConfigKey(configs: ClientConfig[], configKey: string): string[] {
    const normalizedTarget = configKey.trim().toLowerCase().replace(/[_\s-]/g, '');
    const values = configs
      .filter((config) => {
        const key = (config.configKey ?? '').trim().toLowerCase().replace(/[_\s-]/g, '');
        return key === normalizedTarget;
      })
      .flatMap((config) => config.configValues ?? [])
      .map((value) => value?.trim())
      .filter((value): value is string => Boolean(value));

    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  }

  private hydrateEditDropdownsFromCurrentReferral(): void {
    const currentTypeOfReferral = this.referral?.typeOfReferral?.trim();
    const currentServices = this.referral?.services ?? [];
    const currentServiceTypes = currentServices
      .map((service) => service.serviceType?.trim())
      .filter((value): value is string => Boolean(value));
    const currentActions = currentServices
      .map((service) => service.action?.trim())
      .filter((value): value is string => Boolean(value));

    if (currentTypeOfReferral) {
      this.typeOfReferralOptions = Array.from(new Set([...this.typeOfReferralOptions, currentTypeOfReferral]))
        .sort((a, b) => a.localeCompare(b));
    }

    if (currentServiceTypes.length) {
      this.serviceTypeOptions = Array.from(new Set([...this.serviceTypeOptions, ...currentServiceTypes]))
        .sort((a, b) => a.localeCompare(b));
    }

    if (currentActions.length) {
      this.actionOptions = Array.from(new Set([...this.actionOptions, ...currentActions]))
        .sort((a, b) => a.localeCompare(b));
    }
  }

  private getConfigValues(configs: ClientConfig[], aliases: string[]): string[] {
    const normalized = aliases.map(a => a.trim().toLowerCase().replace(/[_\s-]/g, ''));

    const matched = configs.find(c => {
      const key = (c.configKey ?? '').trim().toLowerCase().replace(/[_\s-]/g, '');
      const name = (c.configName ?? '').trim().toLowerCase().replace(/[_\s-]/g, '');
      return normalized.includes(key) || normalized.includes(name);
    });

    if (!matched || !Array.isArray(matched.configValues)) return [];

    return matched.configValues
      .map(v => v?.trim())
      .filter((v): v is string => Boolean(v))
      .sort((a, b) => a.localeCompare(b));
  }

  private createEmptyService(): ReferralDetailService {
    return {
      serviceType: null,
      action: null,
      serviceDataID: null,
      invoiceAmount: null,
      comments: null,
    };
  }

  private matchOption(value: string, options: string[]): string {
    const exact = options.find(opt => opt === value);
    if (exact) return exact;
    const lower = value.trim().toLowerCase();
    return options.find(opt => opt.trim().toLowerCase() === lower) ?? value;
  }

  public isDetailsFieldInvalid(field: 'typeOfReferral' | 'claimNumber'): boolean {
    if (!this.showValidationErrors || !this.editModel) {
      return false;
    }

    return this.isMissingRequiredValue(this.editModel[field]);
  }

  private isRequiredFieldsMissing(): boolean {
    return !this.editModel
      || this.isMissingRequiredValue(this.editModel.typeOfReferral)
      || this.isMissingRequiredValue(this.editModel.claimNumber);
  }

  private isMissingRequiredValue(value: string | null | undefined): boolean {
    return !value || value.trim().length === 0;
  }
}
